import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, DollarSign, Receipt, Store, PieChart as PieChartIcon, Activity, Calendar, BarChart3, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, ComposedChart
} from 'recharts';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [expensesByStore, setExpensesByStore] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [ivaSummary, setIvaSummary] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAllData();
  }, [dateRange]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch analytics
      const analyticsRes = await axios.get(`${API}/dashboard/analytics`, {
        params: dateRange,
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(analyticsRes.data);

      // Fetch income statement for waterfall
      const incomeRes = await axios.get(`${API}/reports/income-statement`, {
        params: { ...dateRange, store_id: 'all' },
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncomeStatement(incomeRes.data);

      // Fetch expenses by store for pie chart
      const expensesRes = await axios.get(`${API}/reports/expenses-by-store`, {
        params: dateRange,
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpensesByStore(expensesRes.data);

      // Fetch profitability for bar chart
      const profitRes = await axios.get(`${API}/reports/profitability-by-store`, {
        params: dateRange,
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfitability(profitRes.data);

      // Fetch IVA summary
      const ivaRes = await axios.get(`${API}/reports/iva-summary`, {
        params: dateRange,
        headers: { Authorization: `Bearer ${token}` }
      });
      setIvaSummary(ivaRes.data);

    } catch (error) {
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">Cargando datos...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-lg text-slate-600 dark:text-slate-400">No hay datos disponibles</p>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">Intenta cambiar el rango de fechas</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const kpis = analytics?.kpis || {};
  const charts = analytics?.charts || {};
  const period = analytics?.period || {};

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Dashboard Financiero</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Análisis integral de ingresos, gastos y rentabilidad</p>
        </div>
        <Card className="bg-card shadow-sm border-slate-200 dark:border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dateRange.start_date}
                  onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                  className="text-sm dark:bg-slate-900 dark:border-slate-600"
                />
                <span className="text-slate-600 dark:text-slate-400">-</span>
                <Input
                  type="date"
                  value={dateRange.end_date}
                  onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                  className="text-sm dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
        <Card className="kpi-emerald border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-white">Ingresos Totales</CardTitle>
            <DollarSign className="h-5 w-5 text-white/80" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {formatCurrency(kpis.total_ingresos || 0)}
            </div>
            <p className="text-xs text-emerald-100 mt-1">{kpis.num_transacciones || 0} transacciones</p>
          </CardContent>
        </Card>

        <Card className="kpi-amber border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-white">Costos</CardTitle>
            <TrendingDown className="h-5 w-5 text-white/80" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {formatCurrency(kpis.total_costos || 0)}
            </div>
            <p className="text-xs text-amber-100 mt-1">Costo de ventas</p>
          </CardContent>
        </Card>

        <Card className="kpi-rose border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-white">Gastos</CardTitle>
            <Receipt className="h-5 w-5 text-white/80" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {formatCurrency(kpis.total_gastos || 0)}
            </div>
            <p className="text-xs text-rose-100 mt-1">Gastos operativos</p>
          </CardContent>
        </Card>

        <Card className="kpi-cyan border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-white">Disponible</CardTitle>
            <DollarSign className="h-5 w-5 text-white/80" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {formatCurrency((kpis.total_ingresos || 0) - (kpis.total_gastos || 0))}
            </div>
            <p className="text-xs text-cyan-100 mt-1">Ingresos - Gastos</p>
          </CardContent>
        </Card>

        <Card className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${(kpis.total_utilidad || 0) >= 0 ? 'kpi-blue' : 'kpi-rose'}`}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-white">Utilidad Neta</CardTitle>
            <Activity className="h-5 w-5 text-white/80" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {formatCurrency(kpis.total_utilidad || 0)}
            </div>
            <p className={`text-xs mt-1 ${(kpis.total_utilidad || 0) >= 0 ? 'text-blue-100' : 'text-red-100'}`}>
              Margen: {(kpis.margen_promedio || 0).toFixed(1)}%
            </p>
          </CardContent>
        </Card>

        <Card className="kpi-purple border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-white">Tiendas Activas</CardTitle>
            <Store className="h-5 w-5 text-white/80" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {kpis.num_tiendas || 0}
            </div>
            <p className="text-xs text-purple-100 mt-1">En operación</p>
          </CardContent>
        </Card>

        {/* IVA por Cobrar */}
        <Card className="kpi-teal border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1" title="IVA que cobraste a tus clientes en las ventas con factura">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-white">IVA Débito Fiscal</CardTitle>
            <Receipt className="h-4 w-4 text-white/80" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-white">
              {ivaSummary ? formatCurrency(ivaSummary.resumen.iva_por_cobrar) : 'Q 0.00'}
            </div>
            <p className="text-xs text-teal-100 mt-1">De tus Ventas</p>
          </CardContent>
        </Card>

        {/* IVA por Pagar */}
        <Card className="kpi-pink border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1" title="IVA que pagaste en compras y gastos (crédito fiscal)">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-medium text-white">IVA Crédito Fiscal</CardTitle>
            <Receipt className="h-4 w-4 text-white/80" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-white">
              {ivaSummary ? formatCurrency(ivaSummary.resumen.iva_por_pagar) : 'Q 0.00'}
            </div>
            <p className="text-xs text-pink-100 mt-1">De tus Compras</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart - Ventas por Tienda */}
        <Card className="bg-card border dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
              <Store className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
              Ventas por Tienda
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer 
              width="100%" 
              height={Math.max(300, (charts.sales_by_store?.length || 0) * 40)}
            >
              <BarChart 
                data={charts.sales_by_store || []}
                layout={(charts.sales_by_store?.length || 0) > 8 ? "vertical" : "horizontal"}
                margin={(charts.sales_by_store?.length || 0) > 8 
                  ? { top: 20, right: 30, left: 150, bottom: 20 }
                  : { top: 20, right: 30, left: 20, bottom: 80 }
                }
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
                {(charts.sales_by_store?.length || 0) > 8 ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 11 }} className="fill-slate-600 dark:fill-slate-400" />
                    <YAxis 
                      type="category"
                      dataKey="store_name" 
                      tick={{ fontSize: 11 }}
                      width={140}
                      className="fill-slate-600 dark:fill-slate-400"
                    />
                  </>
                ) : (
                  <>
                    <XAxis 
                      dataKey="store_name" 
                      tick={{ fontSize: 11 }} 
                      angle={-35} 
                      textAnchor="end" 
                      height={90}
                      className="fill-slate-600 dark:fill-slate-400"
                    />
                    <YAxis tick={{ fontSize: 11 }} className="fill-slate-600 dark:fill-slate-400" />
                  </>
                )}
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)', borderRadius: '8px' }}
                  labelStyle={{ color: 'var(--foreground)' }}
                />
                <Legend />
                <Bar dataKey="ventas" name="Ventas" fill="#10b981" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfica removida: Distribución de Gastos por Categoría - No aportaba valor */}
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 gap-6">
        {/* Line Chart - Tendencia Ingresos vs Gastos */}
        <Card className="bg-card border dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
              <Activity className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
              Tendencia: Ingresos vs Gastos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={charts.trend_data || []}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
                <XAxis 
                  dataKey="fecha" 
                  tick={{ fontSize: 11 }} 
                  angle={-15}
                  textAnchor="end"
                  height={70}
                  className="fill-slate-600 dark:fill-slate-400"
                />
                <YAxis tick={{ fontSize: 11 }} className="fill-slate-600 dark:fill-slate-400" />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)', borderRadius: '8px' }}
                  labelStyle={{ color: 'var(--foreground)' }}
                />
                <Legend />
                <Line type="monotone" dataKey="ingresos" name="Ingresos" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="gastos" name="Gastos" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 3 */}
      <div className="grid grid-cols-1 gap-6">
        {/* Area Chart - Rentabilidad por Tienda */}
        <Card className="bg-card border dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
              Rentabilidad por Tienda
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer 
              width="100%" 
              height={Math.max(350, (charts.profitability_by_store?.length || 0) * 50)}
            >
              <BarChart 
                data={charts.profitability_by_store || []}
                layout={(charts.profitability_by_store?.length || 0) > 8 ? "vertical" : "horizontal"}
                margin={(charts.profitability_by_store?.length || 0) > 8
                  ? { top: 20, right: 30, left: 150, bottom: 20 }
                  : { top: 20, right: 30, left: 20, bottom: 80 }
                }
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
                {(charts.profitability_by_store?.length || 0) > 8 ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 11 }} className="fill-slate-600 dark:fill-slate-400" />
                    <YAxis 
                      type="category"
                      dataKey="store_name" 
                      tick={{ fontSize: 11 }}
                      width={140}
                      className="fill-slate-600 dark:fill-slate-400"
                    />
                  </>
                ) : (
                  <>
                    <XAxis 
                      dataKey="store_name" 
                      tick={{ fontSize: 11 }} 
                      angle={-35}
                      textAnchor="end"
                      height={90}
                      className="fill-slate-600 dark:fill-slate-400"
                    />
                    <YAxis tick={{ fontSize: 11 }} className="fill-slate-600 dark:fill-slate-400" />
                  </>
                )}
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)', borderRadius: '8px' }}
                  labelStyle={{ color: 'var(--foreground)' }}
                />
                <Legend />
                <Bar dataKey="ingresos" name="Ingresos" fill="#10b981" stackId="a" />
                <Bar dataKey="gastos" name="Gastos" fill="#ef4444" stackId="a" />
                <Bar dataKey="utilidad" name="Utilidad Neta" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 4: Gráficas de Reportes */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        {/* Gráfica Waterfall removida - No aportaba información útil */}

        {/* Pie Chart - Distribución de Gastos por Tienda */}
        {expensesByStore && Object.values(expensesByStore.expenses_by_store || {}).length > 0 && (
          <Card className="bg-card border dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
                <PieChartIcon className="h-5 w-5 mr-2 text-orange-600 dark:text-orange-400" />
                Distribución de Gastos por Tienda
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={380}>
                <PieChart>
                  <Pie
                    data={Object.values(expensesByStore.expenses_by_store)
                      .sort((a, b) => b.total_expenses - a.total_expenses)
                      .slice(0, 10)
                      .map(store => ({
                        name: store.store_name,
                        value: store.total_expenses
                      }))}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={(entry) => {
                      const total = Object.values(expensesByStore.expenses_by_store).reduce((sum, s) => sum + s.total_expenses, 0);
                      return `${entry.name}: ${((entry.value / total) * 100).toFixed(1)}%`;
                    }}
                    outerRadius={110}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.values(expensesByStore.expenses_by_store)
                      .sort((a, b) => b.total_expenses - a.total_expenses)
                      .slice(0, 10)
                      .map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)', borderRadius: '8px' }}
                    labelStyle={{ color: 'var(--foreground)' }}
                  />
                  <Legend 
                    layout="vertical" 
                    align="right" 
                    verticalAlign="middle"
                    wrapperStyle={{ fontSize: '11px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {Object.values(expensesByStore.expenses_by_store).length > 10 && (
                <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-2">
                  Mostrando las 10 tiendas con más gastos
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Charts Row 5: Rentabilidad Comparativo */}
      {profitability && profitability.by_store && profitability.by_store.length > 0 && (
        <div className="grid grid-cols-1 gap-6">
          <Card className="bg-card border dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
                Comparativo de Rentabilidad por Tienda
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer 
                width="100%" 
                height={Math.max(400, profitability.by_store.length * 50)}
              >
                <BarChart 
                  data={profitability.by_store}
                  layout={profitability.by_store.length > 8 ? "vertical" : "horizontal"}
                  margin={profitability.by_store.length > 8
                    ? { top: 20, right: 30, left: 150, bottom: 20 }
                    : { top: 20, right: 30, left: 20, bottom: 90 }
                  }
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
                  {profitability.by_store.length > 8 ? (
                    <>
                      <XAxis type="number" tick={{ fontSize: 11 }} className="fill-slate-600 dark:fill-slate-400" />
                      <YAxis 
                        type="category"
                        dataKey="store_name" 
                        tick={{ fontSize: 11 }}
                        width={140}
                        className="fill-slate-600 dark:fill-slate-400"
                      />
                    </>
                  ) : (
                    <>
                      <XAxis 
                        dataKey="store_name" 
                        angle={-35}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 11 }}
                        className="fill-slate-600 dark:fill-slate-400"
                      />
                      <YAxis tick={{ fontSize: 11 }} className="fill-slate-600 dark:fill-slate-400" />
                    </>
                  )}
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)', borderRadius: '8px' }}
                    labelStyle={{ color: 'var(--foreground)' }}
                  />
                  <Legend />
                  <Bar dataKey="ingresos" name="Ingresos" fill="#10b981" radius={profitability.by_store.length > 8 ? [0, 4, 4, 0] : [4, 4, 0, 0]} />
                  <Bar dataKey="gastos" name="Gastos" fill="#ef4444" radius={profitability.by_store.length > 8 ? [0, 4, 4, 0] : [4, 4, 0, 0]} />
                  <Bar dataKey="utilidad_neta" name="Utilidad Neta" fill="#3b82f6" radius={profitability.by_store.length > 8 ? [0, 4, 4, 0] : [4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
